'use client'
import React, { FC, memo } from 'react'
import { BackToTop } from '../../parts/back-to-top'
import { Contact } from '../../parts/contact'
import { ColumnHeader } from '../../parts/column-header'
import Link from 'next/link'
import { ColumnSideNavBox } from '../../parts/column-side-nav-box'
import { ColumnBanner } from '../../parts/column-banner/index'
import { ColumnLayout } from '../../layouts/column-layout'
import clsx from 'clsx'
import { ColumnSideNavCategoryList } from '../../parts/column-side-nav-category-list'
import { Social } from '../../parts/sns-detail'
import {
  getColumn,
  getColumns,
  getColumnCategoryBySlug,
  getSeminarList,
  newtClient
} from '../../../utils/newt/newt'
import { ColumnList } from '../../newt/templates/ColumnList'
import type { Column, ColumnCategory } from '../../../types/newt/column'
import { Seminar } from '../../../types/newt/seminar'
// import { Pagination } from '../../newt/parts/Pagination'

type PageColumnArchive = {
  title: string
  categories: ColumnCategory[]
  categoryCounts: { [key: string]: number }
  column: Column[]
  seminar: Seminar[]
  activeCategoryId?: string
  total: number
  path: string
}

export const ColumnArchiveTemplate: FC<PageColumnArchive> = memo(
  ({
    title,
    categories,
    categoryCounts,
    column,
    seminar,
    activeCategoryId,
    total
  }: PageColumnArchive) => {
    const isCategoryPage =
      activeCategoryId !== undefined || title ? true : false

    // categoriesとcategoryCountsを用いて新しい配列を作成
    const sortedCategories = categories
      .map((category) => {
        return {
          ...category,
          count: categoryCounts[category._id] || 0
        }
      })
      .sort((a, b) => a.count - b.count)

    return (
      <>
        <ColumnHeader />
        <div className={'flex justify-center py-5 px-6 lg:hidden'}>
          <ColumnBanner />
        </div>
        <ColumnLayout
          content={
            <>
              <div className="mt-[34px] lg:mt-16"></div>
              {isCategoryPage && title ? (
                <h2
                  className={
                    'bg-lawguebase flex flex-col gap-y-[10px] lg:gap-x-2 lg:flex-row lg:items-center rounded-[8px] py-2 px-5 mt-[34px] lg:mt-16'
                  }
                >
                  {activeCategoryId !== undefined ? (
                    <span
                      className={
                        'text-xs leading-normal lg:leading-[18px] lg:flex gap-x-2'
                      }
                    >
                      カテゴリー
                    </span>
                  ) : (
                    <span
                      className={
                        'text-xs leading-normal lg:leading-[18px] lg:flex gap-x-2'
                      }
                    >
                      タグ
                    </span>
                  )}
                  <span className={'hidden lg:block text-base leading-[18px]'}>
                    ：
                  </span>
                  <p
                    className={
                      'flex flex-wrap gap-x-6 text-base leading-[2] lg:flex-1'
                    }
                  >
                    {title}
                  </p>
                </h2>
              ) : (
                <div className={'mt-[34px] lg:mt-16'}></div>
              )}
              <ul
                className={
                  'flex lg:hidden whitespace-nowrap overflow-auto text-sm leading-normal font-bold mt-6 -mx-6 px-6'
                }
              >
                <li>
                  <Link
                    href={`/column/`}
                    className={clsx(
                      'block rounded-full py-2 px-4',
                      !activeCategoryId && 'bg-primary text-white'
                    )}
                  >
                    すべて
                  </Link>
                </li>
                {categories.map((category) => (
                  <li key={category._id}>
                    <Link
                      href={`/column/categories/${category.slug}/`}
                      className={clsx(
                        'block rounded-full py-2 px-4',
                        activeCategoryId === category._id &&
                          'bg-primary text-white'
                      )}
                    >
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <ul
                className={
                  'grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] xl:grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-x-[60px] gap-y-8 lg:gap-y-10 mt-6 lg:mt-10'
                }
              >
                {column.map((col) => (
                  <ColumnList key={col._id} column={col} />
                ))}
              </ul>
              {/*
              // TODO コラム一覧の件数が増えたらページネーション実装
              <div className={'mt-16 lg:mt-10'}>
                <Pagination
                  total={total}
                  current={1}
                  basePath={'/column/page'}
                  contentType="column"
                />
              </div> */}
            </>
          }
          sideNav={
            <>
              {categories && (
                <ColumnSideNavCategoryList
                  categories={sortedCategories}
                  categoryCounts={categoryCounts}
                />
              )}
              {/* // TODO タグ機能が増えたら実装
              {tags.length > 0 && <ColumnSideNavTagList tags={tags} />} */}
              {seminar && seminar.length > 0 && (
                <ColumnSideNavBox label={'セミナー情報'}>
                  <Link
                    href={`/seminar/${seminar[0].slug}/`}
                    className={'block overflow-hidden group/item mt-6'}
                  >
                    <div
                      className={'aspect-[1200/743] overflow-hidden rounded-lg'}
                    >
                      <img
                        className={
                          'w-full h-full object-cover transition-transform group-hover/item:scale-110 duration-500 ease-in-out will-change-transform rounded-lg'
                        }
                        src={
                          seminar[0].eyecatch
                            ? seminar[0].eyecatch.src
                            : 'デフォルトの画像URL'
                        }
                        alt={seminar[0].title || 'セミナー情報'}
                      />
                    </div>
                  </Link>
                </ColumnSideNavBox>
              )}
              <div className={'-mx-6'}>
                <Social />
              </div>
            </>
          }
        />
        <BackToTop />
        <Contact bg="bg-lawguebase" />
      </>
    )
  }
)

export const getStaticProps = async (context) => {
  const { total, categories } = await getColumn({
    client: newtClient('cdn'),
    query: { limit: 99 },
    includeCategories: true
  })

  const { slug } = context.params
  const columnCategory = await getColumnCategoryBySlug(slug)
  let columns: Column[] = []

  if (columnCategory) {
    columns = await getColumns(columnCategory._id)
  }

  // セミナー一覧を取得
  const { seminar: seminarList } = await getSeminarList({
    client: newtClient('cdn')
  })

  return {
    props: {
      columns,
      categories,
      columnCategory,
      total,
      seminar: seminarList
    }
  }
}

ColumnArchiveTemplate.displayName = 'PageColumnArchive'
